import type { OwnCredit } from '../../jsonld'
import { MEDIA_BASE } from '../../routes'

/**
 * Responsive-image paths for Verde Vercelli 2.
 *
 * `scripts/media/render-posters.mjs` (container; its frames come from
 * `scripts/media/extract-posters.sh` on the host) writes every manifest role as
 * `<role>-<width>.<ext>` under `sede-public/<slug>/media/<slug>/`, which
 * is served at {@link MEDIA_BASE}. These helpers are the only place that
 * naming scheme is spelled out on the client side: `Picture`, `Credit`
 * and the hero preload in head.ts all go through here.
 *
 * Pure module — imported by head.ts in the Node prerender. It may import
 * routes.ts and TYPES only; anything heavier would pull the page tree into
 * the head and risk an import cycle.
 */

/** Re-exported, not redeclared: routes.ts owns the media root. */
export { MEDIA_BASE }

/** Widths the poster script writes for every role — all four, always, so no srcset candidate 404s. */
export const WIDTHS = [640, 1024, 1600, 2400] as const

export type MediaExt = 'avif' | 'webp' | 'jpg'

/** `/media/verde-vercelli-2/<role>-<width>.<ext>` */
export function srcFor(role: string, width: number, ext: MediaExt): string {
  return `${MEDIA_BASE}/${role}-${width}.${ext}`
}

/** `…/<role>-640.<ext> 640w, …/<role>-1024.<ext> 1024w, …` */
export function srcSetFor(role: string, ext: MediaExt, widths: readonly number[] = WIDTHS): string {
  return widths.map((w) => `${srcFor(role, w, ext)} ${w}w`).join(', ')
}

/**
 * Intrinsic size of the role's reference rendition (from credits.json), so
 * `<img width height>` reserves the right box before the file arrives.
 * 1600×900 when the role has no credit yet (media not fetched).
 */
export function dimensionsFor(role: string, credits: readonly OwnCredit[]): { width: number; height: number } {
  const c = credits.find((x) => x.role === role)
  return c && c.width > 0 && c.height > 0 ? { width: c.width, height: c.height } : { width: 1600, height: 900 }
}
